import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import axiosInstance from './api/axios';

const UploadProgressContext = createContext();

export const useUploadProgress = () => useContext(UploadProgressContext);

export const UploadProgressProvider = ({ children }) => {
    const [jobs, setJobs] = useState([]);
    const timers = useRef({});

    const updateJob = useCallback((id, updates) => {
        setJobs(prev => prev.map(j => j.id === id ? { ...j, ...updates } : j));
    }, []);

    const pollJob = useCallback((id, jobId) => {
        const poll = async () => {
            // If dismissed while this request was in-flight, do not re-schedule
            if (timers.current[id] === 'cancelled') return;
            try {
                const { data } = await axiosInstance.get(`/api/upload-status/${jobId}`);
                if (timers.current[id] === 'cancelled') return;
                if (data.status === 'completed' || data.status === 'error') {
                    updateJob(id, { status: data.status, progress: 100, result: data });
                    delete timers.current[id];
                } else {
                    updateJob(id, { progress: data.progress || 0 });
                    timers.current[id] = setTimeout(poll, 1500);
                }
            } catch (err) {
                if (timers.current[id] === 'cancelled') return;
                if (err.response?.status === 401) {
                    updateJob(id, { status: 'error', result: { detail: 'Session expired.' } });
                    delete timers.current[id];
                } else {
                    timers.current[id] = setTimeout(poll, 1500);
                }
            }
        };
        timers.current[id] = setTimeout(poll, 1000);
    }, [updateJob]);

    const addJob = useCallback((jobInfo) => {
        const id = crypto.randomUUID();
        setJobs(prev => [...prev, {
            id,
            status: 'processing',
            progress: 0,
            result: null,
            ...jobInfo,
        }]);
        pollJob(id, jobInfo.jobId);
        return id;
    }, [pollJob]);

    const dismissJob = useCallback((id) => {
        if (timers.current[id]) {
            clearTimeout(timers.current[id]);
        }
        // Keep the 'cancelled' marker so any in-flight poll callback sees it and stops
        timers.current[id] = 'cancelled';
        setJobs(prev => prev.filter(j => j.id !== id));
    }, []);

    return (
        <UploadProgressContext.Provider value={{ jobs, addJob, dismissJob }}>
            {children}
        </UploadProgressContext.Provider>
    );
};
